import { useEffect, useState } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import { Receipt, Plus, Search, Edit2, Trash2, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getSupabaseClient } from "../providers/supabase/supabase";

type Expense = {
  id: number;
  category: string;
  amount: number;
  currency: string;
  description: string | null;
  status: string;
  submitted_by: number | null;
  approved_by: number | null;
  expense_date: string;
  notes: string | null;
  created_at: string;
  submitter?: { first_name: string; last_name: string } | null;
};

const STATUS_BADGE: Record<string, string> = {
  pending:    "bg-yellow-100 text-yellow-700",
  approved:   "bg-green-100 text-green-700",
  rejected:   "bg-red-100 text-red-700",
  reimbursed: "bg-blue-100 text-blue-700",
};

const CATEGORIES: Record<string, string> = {
  trade_show: "Trade Show",
  sample:     "Sample",
  travel:     "Travel",
  shipping:   "Shipping",
  marketing:  "Marketing",
  other:      "Other",
};

const emptyForm = () => ({
  category: "other",
  amount: "",
  currency: "USD",
  description: "",
  expense_date: new Date().toISOString().slice(0, 10),
  notes: "",
});

const ExpensesPage = () => {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = () => {
    getSupabaseClient()
      .from("expenses")
      .select("*, submitter:sales!expenses_submitted_by_fkey(first_name, last_name)")
      .order("expense_date", { ascending: false })
      .then(({ data }) => { setExpenses(data as Expense[] ?? []); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (e: Expense) => {
    setEditing(e);
    setForm({
      category: e.category, amount: e.amount.toString(),
      currency: e.currency, description: e.description ?? "",
      expense_date: e.expense_date, notes: e.notes ?? "",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.amount) { notify("Amount is required", { type: "error" }); return; }
    setSaving(true);
    const supabase = getSupabaseClient();
    const payload = {
      category: form.category, amount: parseFloat(form.amount),
      currency: form.currency, description: form.description || null,
      expense_date: form.expense_date, notes: form.notes || null,
      updated_at: new Date().toISOString(),
    };
    if (editing) {
      const { error } = await supabase.from("expenses").update(payload).eq("id", editing.id);
      if (error) { notify(error.message, { type: "error" }); setSaving(false); return; }
      notify("Expense updated", { type: "info" });
    } else {
      const { error } = await supabase.from("expenses").insert({ ...payload, submitted_by: identity?.id ?? null, status: "pending" });
      if (error) { notify(error.message, { type: "error" }); setSaving(false); return; }
      notify("Expense submitted", { type: "info" });
    }
    setSaving(false); setShowForm(false); load();
  };

  const updateStatus = async (id: number, status: string) => {
    await getSupabaseClient().from("expenses").update({
      status,
      approved_by: identity?.id ?? null,
      updated_at: new Date().toISOString(),
    }).eq("id", id);
    notify(`Expense ${status}`, { type: "info" }); load();
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this expense?")) return;
    await getSupabaseClient().from("expenses").delete().eq("id", id);
    notify("Deleted", { type: "info" }); load();
  };

  const filtered = expenses.filter(e => {
    if (statusFilter !== "all" && e.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (e.description ?? "").toLowerCase().includes(q) || CATEGORIES[e.category]?.toLowerCase().includes(q);
    }
    return true;
  });

  const totalByStatus = (s: string) =>
    expenses.filter(e => e.status === s && e.currency === "USD").reduce((a, b) => a + b.amount, 0);

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Receipt className="w-6 h-6" /> Expenses</h1>
        <Button size="sm" onClick={openCreate} className="gap-1"><Plus className="w-3.5 h-3.5" /> Submit Expense</Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[["pending","Pending","text-yellow-600"],["approved","Approved","text-green-600"],["reimbursed","Reimbursed","text-blue-600"]].map(([s,l,cls]) => (
          <div key={s} className="bg-card border rounded-lg p-4">
            <div className="text-xs text-muted-foreground mb-1">{l} (USD)</div>
            <div className={`text-xl font-bold tabular-nums ${cls}`}>${totalByStatus(s).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
          <Input className="pl-8 h-8 text-sm" placeholder="Description, category..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex border rounded-lg overflow-hidden">
          {["all","pending","approved","rejected","reimbursed"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${statusFilter === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Receipt className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>No expenses found.</p>
        </div>
      ) : (
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-xs text-muted-foreground uppercase">
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Category</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">Description</th>
                <th className="text-right px-4 py-2.5">Amount</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">Submitted by</th>
                <th className="text-center px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id} className="border-t hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 text-muted-foreground">{new Date(e.expense_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3">{CATEGORIES[e.category] ?? e.category}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{e.description ?? "—"}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">{e.currency} {e.amount.toLocaleString()}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">
                    {e.submitter ? `${e.submitter.first_name} ${e.submitter.last_name}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_BADGE[e.status] ?? ""}`}>{e.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      {e.status === "pending" && (
                        <>
                          <button title="Approve" onClick={() => updateStatus(e.id, "approved")} className="p-1 text-green-600 hover:text-green-700"><CheckCircle className="w-3.5 h-3.5" /></button>
                          <button title="Reject" onClick={() => updateStatus(e.id, "rejected")} className="p-1 text-red-500 hover:text-red-600"><XCircle className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                      {e.status === "approved" && (
                        <button title="Mark Reimbursed" onClick={() => updateStatus(e.id, "reimbursed")}
                          className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Reimburse</button>
                      )}
                      <button onClick={() => openEdit(e)} className="p-1 text-muted-foreground hover:text-foreground"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(e.id)} className="p-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={o => !o && setShowForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit Expense" : "Submit Expense"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => set("category", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input className="h-8 text-sm" type="date" value={form.expense_date} onChange={e => set("expense_date", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Amount *</Label>
              <Input className="h-8 text-sm" type="number" step="0.01" placeholder="0.00" value={form.amount} onChange={e => set("amount", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={v => set("currency", v)}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["USD","EUR","GBP","CNY"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Description</Label>
              <Input className="h-8 text-sm" placeholder="Brief description..." value={form.description} onChange={e => set("description", e.target.value)} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Textarea className="text-sm resize-none" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Submit"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
